import React from 'react';

import { StepProps } from '@betterment/js-runtime';
import { Icon } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Button, CenteredWorkflowLayout, HR, Label, ListRow, Tooltip } from '@betterment/style-closet-react';
import { Form, Formik } from 'formik';

import { WithdrawalValues } from './Controller';

import { AmountSchema } from '../Checking/amount-schema';
import { useAccount } from '../Checking/components/AccountProvider';
import { Customer } from '../Checking/data';
import { InputGroupWithLabelAndError } from '../Checking/form-helpers';
import { intoBettermentAccount } from '../Checking/models/account';
import { intoBankAccount } from '../Checking/models/external-account';
import { externalAccountCreationPath } from '../Checking/path-helpers';
import { trackClick } from '../features/track';
import { AccountOption } from '../Shared/components/AccountOption';
import { AccountSuspendedBanner } from '../Shared/components/AccountSuspendedBanner';
import Footer from '../Shared/components/Footer';
import { FormikNumberInput } from '../Shared/components/NumberInput';
import { WorkflowCard } from '../Shared/components-v2/workflow-card/WorkflowCard';

interface Props extends StepProps<WithdrawalValues> {
  customer: Customer;
}

const WithdrawalFlowAmount: React.FC<React.PropsWithChildren<Props>> = ({ flowState, goToNextStep, customer }) => {
  const { account } = useAccount();

  if (customer.externalAccount == null) return null;

  const isAccountSuspended = customer.isSuspended;
  const isExternalAccountLocked = customer.externalAccount.isLocked || false;

  return (
    <CenteredWorkflowLayout title="Withdrawal" heading="Set up withdrawal">
      <CenteredWorkflowLayout.Slot name="content">
        {isAccountSuspended && <AccountSuspendedBanner />}
        <Formik
          initialValues={flowState.values}
          onSubmit={goToNextStep}
          validationSchema={AmountSchema}
          validateOnChange={false}
          validateOnBlur={false}
        >
          <Form>
            <WorkflowCard variant="compact" css={{ marginBottom: 'space.300' }}>
              <ListRow>
                <ListRow.Slot name="leading">
                  <Label>From</Label>
                  <AccountOption account={intoBettermentAccount(account, customer)}>
                    <AccountOption.Slot name="content">
                      <Tooltip
                        label="Available balance is money that is ready to use. Withdrawals to your linked bank account and purchases are immediately deducted. Pending deposits will show up in your balance after they have completed."
                        title="more info about your available balance"
                        verticalAlign="middle"
                      >
                        <Icon name="QuestionMarkCircle" size="xxsmall" color="icon.weak" />
                      </Tooltip>
                    </AccountOption.Slot>
                  </AccountOption>
                </ListRow.Slot>
              </ListRow>
              <HR variant="list-row" />
              <ListRow>
                <ListRow.Slot name="leading">
                  <Label>To</Label>
                  <AccountOption account={intoBankAccount(customer.externalAccount)} />
                </ListRow.Slot>
                <ListRow.Slot name="trailing">
                  <SambaAutoLink
                    to={externalAccountCreationPath(account.id, 'withdrawal')}
                    title="Choose a different external account"
                    onClick={() => trackClick({ name: 'EditBank', flow: 'WithdrawFromChecking' })}
                  >
                    Change
                  </SambaAutoLink>
                </ListRow.Slot>
              </ListRow>
            </WorkflowCard>
            <HR variant="workflow-card" />
            <WorkflowCard>
              <InputGroupWithLabelAndError fieldName="amount" labelText="Amount">
                {(fieldProps: { id: string; name: string }) => (
                  <FormikNumberInput
                    {...fieldProps}
                    unit="dollars"
                    autoFocus={true}
                    placeholder="0"
                    mb={3}
                    disabled={isExternalAccountLocked}
                  />
                )}
              </InputGroupWithLabelAndError>
            </WorkflowCard>
            <CenteredWorkflowLayout.Actions>
              <Button type="submit" disabled={isExternalAccountLocked || isAccountSuspended}>
                Review withdrawal
              </Button>
            </CenteredWorkflowLayout.Actions>
          </Form>
        </Formik>
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="footer">
        <Footer />
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default WithdrawalFlowAmount;
